import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import Button from '../../../components/atoms/forms/Button';

// One checklist item / accordion section.
// Owns its expand/collapse state internally.
// Auto-collapses when `completed` flips to true or `disabled` is set.
// Renders Cancel + Mark Complete in the body footer.

const StyledDiv = styledComponent('div', {
  background: theme.colors.bgCard,
  border: theme.borders.card,
  borderRadius: theme.radii.card,
  marginBottom: theme.spacing.sectionGap,
  overflow: 'hidden',

  '&.is-completed': {
    borderColor: theme.colors.primary,
  },
  '&.is-disabled': {
    opacity: 0.55,
    background: theme.colors.bgMuted,
  },

  // Native <button> for the section header. Reset the user-agent button
  // styling to keep the layout/cursor look; `disabled` on the element handles
  // tabIndex + click-blocking natively.
  '& .cs-header': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.fieldGap,
    padding: theme.spacing.bodyPadding,
    cursor: 'pointer',
    userSelect: 'none',
    width: '100%',
    background: 'transparent',
    border: 0,
    textAlign: 'left',
    font: 'inherit',
    color: 'inherit',
  },
  '& .cs-header:focus-visible': {
    outline: `2px solid ${theme.colors.infoBlue}`,
    outlineOffset: '-2px',
  },
  '& .cs-header:disabled': {
    cursor: 'not-allowed',
  },

  '& .cs-checkbox': {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: `2px solid ${theme.colors.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.white,
    fontSize: theme.typography.body.fontSize,
    fontWeight: theme.fontWeights.semibold,
    flexShrink: 0,
  },
  '&.is-completed .cs-checkbox': {
    background: theme.colors.primary,
    borderColor: theme.colors.primary,
  },

  '& .cs-step': {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: theme.colors.bgMuted,
    color: theme.colors.textMuted,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.typography.body.fontSize,
    fontWeight: theme.fontWeights.semibold,
    flexShrink: 0,
  },
  '&.is-completed .cs-step': {
    background: theme.colors.primary,
    color: theme.colors.white,
  },

  // Header is a native <button> with <span> descendants (HTML5 only allows
  // phrasing content inside <button>). Force the title/desc spans to
  // block-level so they stack vertically.
  '& .cs-content': {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  '& .cs-title': {
    display: 'block',
    fontSize: '16px',
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
    marginBottom: theme.spacing.labelGap,
  },
  '& .cs-desc': {
    display: 'block',
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textMuted,
  },
  '& .cs-expand': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    flexShrink: 0,
  },

  '& .cs-body': {
    padding: theme.spacing.bodyPadding,
    borderTop: `1px solid ${theme.colors.borderLight}`,
  },

  '& .cs-footer': {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing.fieldGap,
    marginTop: theme.spacing.sectionGap,
  },
  // footerAction (when present) hugs the left edge; Cancel / Mark Complete
  // stay pinned right.
  '& .cs-footer-action': {
    marginRight: 'auto',
  },
});

function ChecklistSection({
  stepNumber,
  title,
  description,
  completed,
  disabled,
  disabledTooltip,
  onCancel,
  onMarkComplete,
  markCompleteDisabled,
  footerAction,
  children,
}) {
  const [isExpanded, setIsExpanded] = useState(!completed && !disabled);

  useEffect(() => {
    if (completed || disabled) setIsExpanded(false);
  }, [completed, disabled]);

  const handleHeaderClick = () => {
    if (disabled) return;
    setIsExpanded((prev) => !prev);
  };

  // Always collapse on Mark Complete, even if `completed` was already true.
  // When the section was previously completed and the user re-expanded it to
  // edit, the parent's completed flag doesn't change on a second Mark Complete
  // — React bails on the no-op setState, so the [completed] useEffect below
  // never fires. Owning the collapse here keeps the UI feedback consistent.
  const handleMarkComplete = () => {
    setIsExpanded(false);
    onMarkComplete();
  };

  const stateClassName = [
    completed ? 'is-completed' : '',
    disabled ? 'is-disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <StyledDiv className={stateClassName} data-testid={`section-${stepNumber}`}>
      <button
        type="button"
        className="cs-header"
        onClick={handleHeaderClick}
        disabled={disabled}
        aria-expanded={isExpanded}
        title={disabled ? disabledTooltip : undefined}
      >
        <span className="cs-checkbox" aria-hidden="true">
          {completed ? '✓' : ''}
        </span>
        <span className="cs-step" aria-hidden="true">
          {stepNumber}
        </span>
        <span className="cs-content">
          <span className="cs-title">{title}</span>
          {description && <span className="cs-desc">{description}</span>}
        </span>
        <span className="cs-expand" aria-hidden="true">
          {isExpanded ? '▼' : '▶'}
        </span>
      </button>
      {isExpanded && (
        <div className="cs-body" data-testid="section-body">
          {children}
          <div className="cs-footer">
            {footerAction && (
              <span className="cs-footer-action">{footerAction}</span>
            )}
            <Button
              variant="secondary"
              onClick={onCancel}
              dataTestId="section-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleMarkComplete}
              disabled={markCompleteDisabled}
              dataTestId="section-complete"
            >
              ✓ Mark Complete
            </Button>
          </div>
        </div>
      )}
    </StyledDiv>
  );
}

ChecklistSection.propTypes = {
  stepNumber: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  completed: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledTooltip: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onMarkComplete: PropTypes.func.isRequired,
  markCompleteDisabled: PropTypes.bool,
  footerAction: PropTypes.node,
  children: PropTypes.node,
};

ChecklistSection.defaultProps = {
  description: '',
  completed: false,
  disabled: false,
  disabledTooltip: '',
  markCompleteDisabled: false,
  footerAction: null,
  children: null,
};

export default ChecklistSection;
